import { gql, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import SearchBar from "components/base/SearchBar";
import Spinner from "components/base/Spinner";
import { useMemo, useState } from "react";
import { searchByProp } from "utils/search";

export const FETCH_LAB_TEST_REPORTS = gql`
  query FETCH_LAB_TEST_REPORTS(
    $productIds: [ID]
    $standardIds: [ID]
    $standardNames: [String]
    $expiryStatus: String
    $offset: Int
    $limit: Int
    $q: String
  ) {
    labTestReports(
      productIds: $productIds
      standardIds: $standardIds
      standardNames: $standardNames
      expiryStatus: $expiryStatus
      offset: $offset
      limit: $limit
      q: $q
    ) {
      results {
        id
        title
        reportNumber
        issuer
        issuedDate
        validityMonths
        validUntil
        expiryStatus
        standards {
          id
          name
        }
        fileUrls
      }
    }
  }
`;

function LabTestReportsSelector({
  onSelectTestReport,
  onDone,
  defaultSelectedTestReports = [],
  variables = {},
}) {
  const [searchText, setSearchText] = useState("");
  const [selectedTestReports, setSelectedTestReports] = useState(
    defaultSelectedTestReports,
  );
  const selectedIdSet = useMemo(
    () => new Set(selectedTestReports.map((report) => report.id)),
    [selectedTestReports],
  );
  const { loading, error, data } = useQuery(FETCH_LAB_TEST_REPORTS, {
    variables: { ...variables },
    fetchPolicy: "network-only",
  });

  function toggleTestReport(testReport) {
    if (selectedIdSet.has(testReport.id)) {
      setSelectedTestReports((prev) =>
        prev.filter((item) => item.id !== testReport.id),
      );
      return;
    }
    setSelectedTestReports((prev) => [...prev, testReport]);
  }

  if (loading) return <Spinner />;
  if (error) return <Errors error={error} />;

  const reports = data?.labTestReports?.results
    ? data.labTestReports.results.filter((i) =>
        searchByProp(i, ["title", "reportNumber"], searchText),
      )
    : [];

  return (
    <div className="flex flex-col flex-1 max-h-[60vh]">
      <SearchBar
        bgColor="dark:bg-gray-900"
        autoFocus
        type="text"
        placeholder="Search by title or report number"
        value={searchText}
        onChange={setSearchText}
      />
      <div className="flex-1 mt-4 overflow-auto">
        {reports.map((i) => (
          <div
            key={i.id}
            className="border-b py-2 hover:bg-blue-200 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-3"
            onClick={() =>
              onDone ? toggleTestReport(i) : onSelectTestReport(i)
            }
          >
            {onDone ? (
              <input
                type="checkbox"
                checked={selectedIdSet.has(i.id)}
                onChange={() => toggleTestReport(i)}
                onClick={(e) => e.stopPropagation()}
              />
            ) : null}
            <div className="flex-1">
              {i.reportNumber && (
                <div className="opacity-70">{i.reportNumber}</div>
              )}
              <div>{i.title}</div>
            </div>
          </div>
        ))}
      </div>
      {onDone ? (
        <div className="pt-4 mt-2 border-t dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-xs opacity-70">
              {selectedTestReports.length} report
              {selectedTestReports.length === 1 ? "" : "s"} selected
            </div>
            {selectedTestReports.length > 0 ? (
              <Button
                danger
                className="text-xs"
                onClick={() => setSelectedTestReports([])}
              >
                Clear Selection
              </Button>
            ) : null}
          </div>
          <Button primary onClick={() => onDone(selectedTestReports)}>
            Done
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export default LabTestReportsSelector;
